import * as monaco from 'monaco-editor';
import type { ThemeRegistrationRaw, createHighlighter } from 'shiki';
import { shikiToMonaco } from '@shikijs/monaco';
import { css } from './use-editor-theme.js';

// append alpha (0-1) to a #rrggbb hex color
const alpha = (hex: string, a: number): string =>
	`${hex.slice(0, 7)}${Math.round(a * 255)
		.toString(16)
		.padStart(2, '0')}`;

export const THEME_LIGHT = 'ts-ssh2-light';
export const THEME_DARK = 'ts-ssh2-dark';

// build a TextMate theme object from CSS custom properties
const buildTheme = (variant: 'light' | 'dark'): ThemeRegistrationRaw => ({
	name: variant === 'light' ? THEME_LIGHT : THEME_DARK,
	type: variant,
	settings: [
		// default foreground
		{
			settings: {
				foreground: css('--color-editor-foreground'),
				background: css('--color-editor'),
			},
		},
		// comments
		{
			scope: ['comment', 'punctuation.definition.comment'],
			settings: { foreground: css('--color-token-comment'), fontStyle: 'italic' },
		},
		// keywords
		{
			scope: [
				'keyword',
				'storage',
				'storage.type',
				'storage.modifier',
				'keyword.control',
				'keyword.operator.new',
				'keyword.operator.expression',
				'keyword.operator.logical',
				'keyword.operator.delete',
				'keyword.operator.typeof',
				'keyword.operator.void',
				'keyword.operator.instanceof',
				'keyword.operator.in',
			],
			settings: { foreground: css('--color-token-keyword') },
		},
		// strings
		{
			scope: ['string', 'punctuation.definition.string'],
			settings: { foreground: css('--color-token-string') },
		},
		// functions
		{
			scope: ['entity.name.function', 'support.function', 'meta.function-call'],
			settings: { foreground: css('--color-token-function') },
		},
		// variables / parameters
		{
			scope: ['variable', 'variable.parameter', 'variable.other', 'variable.language'],
			settings: { foreground: css('--color-token-variable') },
		},
		// types / classes
		{
			scope: [
				'entity.name.type',
				'support.type',
				'support.class',
				'entity.name.class',
				'entity.other.inherited-class',
				'storage.type.class',
				'storage.type.interface',
			],
			settings: { foreground: css('--color-token-type') },
		},
		// numbers
		{
			scope: ['constant.numeric'],
			settings: { foreground: css('--color-token-number') },
		},
		// constants (boolean, null, etc.)
		{
			scope: ['constant.language', 'constant.other', 'variable.other.constant'],
			settings: { foreground: css('--color-token-constant') },
		},
		// operators
		{
			scope: ['keyword.operator', 'punctuation.accessor'],
			settings: { foreground: css('--color-token-operator') },
		},
		// tags (HTML/XML)
		{
			scope: ['entity.name.tag', 'punctuation.definition.tag'],
			settings: { foreground: css('--color-token-tag') },
		},
		// attributes (HTML/XML)
		{
			scope: ['entity.other.attribute-name'],
			settings: { foreground: css('--color-token-attribute') },
		},
		// regex
		{
			scope: ['string.regexp'],
			settings: { foreground: css('--color-token-constant') },
		},
		// escape characters
		{
			scope: ['constant.character.escape'],
			settings: { foreground: css('--color-token-constant') },
		},
		// punctuation / brackets — use default foreground
		{
			scope: ['punctuation.separator', 'punctuation.terminator', 'meta.brace'],
			settings: { foreground: css('--color-editor-foreground') },
		},
		// JSON keys
		{
			scope: ['support.type.property-name.json'],
			settings: { foreground: css('--color-token-variable') },
		},
		// decorators / annotations
		{
			scope: ['meta.decorator', 'punctuation.decorator'],
			settings: { foreground: css('--color-token-function') },
		},
		// --- markup (markdown, rst, asciidoc, etc.) ---
		// headings
		{
			scope: ['markup.heading', 'entity.name.section', 'punctuation.definition.heading'],
			settings: { foreground: css('--color-token-function'), fontStyle: 'bold' },
		},
		// bold
		{
			scope: ['markup.bold', 'punctuation.definition.bold'],
			settings: { foreground: css('--color-token-tag'), fontStyle: 'bold' },
		},
		// italic
		{
			scope: ['markup.italic', 'punctuation.definition.italic'],
			settings: { foreground: css('--color-token-variable'), fontStyle: 'italic' },
		},
		// inline code
		{
			scope: [
				'markup.inline.raw',
				'markup.raw',
				'punctuation.definition.raw',
				'markup.fenced_code',
				'fenced_code.block',
			],
			settings: { foreground: css('--color-token-string') },
		},
		// blockquotes
		{
			scope: ['markup.quote', 'punctuation.definition.quote'],
			settings: { foreground: css('--color-token-comment') },
		},
		// list markers
		{
			scope: ['punctuation.definition.list', 'markup.list'],
			settings: { foreground: css('--color-token-keyword') },
		},
		// links
		{
			scope: ['string.other.link', 'markup.underline.link'],
			settings: { foreground: css('--color-token-function'), fontStyle: 'underline' },
		},
		// link title text
		{
			scope: ['string.other.link.title', 'string.other.link.description'],
			settings: { foreground: css('--color-token-function'), fontStyle: '' },
		},
		// link punctuation
		{
			scope: ['punctuation.definition.link', 'punctuation.definition.metadata'],
			settings: { foreground: css('--color-token-comment') },
		},
		// strikethrough
		{
			scope: ['markup.strikethrough'],
			settings: { foreground: css('--color-token-comment'), fontStyle: 'strikethrough' },
		},
		// inserted / deleted (diff)
		{
			scope: ['markup.inserted'],
			settings: { foreground: css('--color-token-string') },
		},
		{
			scope: ['markup.deleted'],
			settings: { foreground: css('--color-token-tag') },
		},
		{
			scope: ['markup.changed'],
			settings: { foreground: css('--color-token-function') },
		},
	],
	colors: {
		// --- editor core ---
		'editor.background': css('--color-editor'),
		'editor.foreground': css('--color-editor-foreground'),
		'editor.selectionBackground': css('--color-selection'),
		'editor.lineHighlightBackground': alpha(css('--color-border'), 0.3),
		'editor.lineHighlightBorder': '#00000000',
		'editorCursor.foreground': css('--color-cursor'),
		'editorCursor.background': css('--color-editor'),
		'editorWhitespace.foreground': css('--color-token-comment'),
		'editorLineNumber.foreground': css('--color-token-comment'),
		'editorLineNumber.activeForeground': css('--color-editor-foreground'),
		'editorLineNumber.dimmedForeground': css('--color-muted-foreground'),
		'editorIndentGuide.background': alpha(css('--color-border'), 0.5),
		'editorIndentGuide.activeBackground': css('--color-border'),
		'editorRuler.foreground': css('--color-border'),
		'editorCodeLens.foreground': css('--color-muted-foreground'),
		'editorGutter.background': css('--color-editor'),
		'editorGutter.foldingControlForeground': css('--color-muted-foreground'),
		'editorLink.activeForeground': css('--color-token-function'),

		// --- bracket highlighting (disable rainbow) ---
		'editorBracketHighlight.foreground1': css('--color-editor-foreground'),
		'editorBracketHighlight.foreground2': css('--color-editor-foreground'),
		'editorBracketHighlight.foreground3': css('--color-editor-foreground'),
		'editorBracketHighlight.foreground4': css('--color-editor-foreground'),
		'editorBracketHighlight.foreground5': css('--color-editor-foreground'),
		'editorBracketHighlight.foreground6': css('--color-editor-foreground'),
		'editorBracketHighlight.unexpectedBracket.foreground': css('--color-destructive'),
		'editorBracketMatch.background': css('--color-selection'),
		'editorBracketMatch.border': css('--color-muted-foreground'),

		// --- bracket pair guides (transparent = hidden) ---
		'editorBracketPairGuide.background1': '#00000000',
		'editorBracketPairGuide.activeBackground1': css('--color-muted-foreground'),
		'editorBracketPairGuide.background2': '#00000000',
		'editorBracketPairGuide.activeBackground2': css('--color-muted-foreground'),
		'editorBracketPairGuide.background3': '#00000000',
		'editorBracketPairGuide.activeBackground3': css('--color-muted-foreground'),
		'editorBracketPairGuide.background4': '#00000000',
		'editorBracketPairGuide.activeBackground4': css('--color-muted-foreground'),
		'editorBracketPairGuide.background5': '#00000000',
		'editorBracketPairGuide.activeBackground5': css('--color-muted-foreground'),
		'editorBracketPairGuide.background6': '#00000000',
		'editorBracketPairGuide.activeBackground6': css('--color-muted-foreground'),

		// --- indent guide numbered variants ---
		'editorIndentGuide.background2': alpha(css('--color-border'), 0.5),
		'editorIndentGuide.activeBackground2': css('--color-border'),
		'editorIndentGuide.background3': alpha(css('--color-border'), 0.5),
		'editorIndentGuide.activeBackground3': css('--color-border'),
		'editorIndentGuide.background4': alpha(css('--color-border'), 0.5),
		'editorIndentGuide.activeBackground4': css('--color-border'),
		'editorIndentGuide.background5': alpha(css('--color-border'), 0.5),
		'editorIndentGuide.activeBackground5': css('--color-border'),
		'editorIndentGuide.background6': alpha(css('--color-border'), 0.5),
		'editorIndentGuide.activeBackground6': css('--color-border'),

		// --- find / replace ---
		'editor.findMatchBackground': css('--color-selection'),
		'editor.findMatchForeground': css('--color-editor-foreground'),
		'editor.findMatchBorder': css('--color-cursor'),
		'editor.findMatchHighlightBackground': css('--color-selection'),
		'editor.findMatchHighlightForeground': css('--color-editor-foreground'),
		'editor.findMatchHighlightBorder': '#00000000',
		'editor.findRangeHighlightBackground': css('--color-line-highlight'),
		'editor.findRangeHighlightBorder': '#00000000',
		'editor.hoverHighlightBackground': css('--color-line-highlight'),
		'editor.rangeHighlightBackground': css('--color-line-highlight'),
		'editor.rangeHighlightBorder': '#00000000',
		'editor.symbolHighlightBackground': css('--color-selection'),
		'editor.symbolHighlightBorder': '#00000000',

		// --- word highlights ---
		'editor.wordHighlightBackground': css('--color-selection'),
		'editor.wordHighlightStrongBackground': css('--color-selection'),
		'editor.wordHighlightTextBackground': css('--color-selection'),
		'editor.wordHighlightBorder': '#00000000',
		'editor.wordHighlightStrongBorder': '#00000000',
		'editor.wordHighlightTextBorder': '#00000000',

		// --- folding ---
		'editor.foldBackground': css('--color-line-highlight'),
		'editor.foldPlaceholderForeground': css('--color-muted-foreground'),

		// --- snippets ---
		'editor.snippetTabstopHighlightBackground': css('--color-selection'),
		'editor.snippetTabstopHighlightBorder': '#00000000',
		'editor.snippetFinalTabstopHighlightBackground': css('--color-selection'),
		'editor.snippetFinalTabstopHighlightBorder': css('--color-cursor'),

		// --- misc editor ---
		'editor.linkedEditingBackground': css('--color-selection'),
		'editor.selectionHighlightBorder': '#00000000',
		'editor.compositionBorder': css('--color-cursor'),
		'editor.placeholder.foreground': css('--color-muted-foreground'),

		// --- diagnostics (error/warning/info/hint squiggles) ---
		'editorError.foreground': css('--color-destructive'),
		'editorError.background': '#00000000',
		'editorError.border': '#00000000',
		'editorWarning.foreground': css('--color-token-type'),
		'editorWarning.background': '#00000000',
		'editorWarning.border': '#00000000',
		'editorInfo.foreground': css('--color-token-function'),
		'editorInfo.background': '#00000000',
		'editorInfo.border': '#00000000',
		'editorHint.foreground': css('--color-muted-foreground'),
		'editorHint.border': '#00000000',
		'editorUnnecessaryCode.opacity': '#000000aa',
		'editorUnnecessaryCode.border': '#00000000',

		// --- ghost text (AI suggestions, etc.) ---
		'editorGhostText.foreground': css('--color-muted-foreground'),
		'editorGhostText.background': '#00000000',
		'editorGhostText.border': '#00000000',

		// --- inlay hints ---
		'editorInlayHint.foreground': css('--color-muted-foreground'),
		'editorInlayHint.background': css('--color-line-highlight'),
		'editorInlayHint.typeForeground': css('--color-muted-foreground'),
		'editorInlayHint.typeBackground': css('--color-line-highlight'),
		'editorInlayHint.parameterForeground': css('--color-muted-foreground'),
		'editorInlayHint.parameterBackground': css('--color-line-highlight'),

		// --- sticky scroll ---
		'editorStickyScroll.background': css('--color-editor'),
		'editorStickyScrollGutter.background': css('--color-editor'),
		'editorStickyScrollHover.background': css('--color-line-highlight'),
		'editorStickyScroll.border': css('--color-border'),
		'editorStickyScroll.shadow': '#00000000',

		// --- overview ruler ---
		'editorOverviewRuler.border': css('--color-border'),
		'editorOverviewRuler.background': css('--color-editor'),
		'editorOverviewRuler.findMatchForeground': css('--color-selection'),
		'editorOverviewRuler.selectionHighlightForeground': css('--color-selection'),
		'editorOverviewRuler.errorForeground': css('--color-destructive'),
		'editorOverviewRuler.warningForeground': css('--color-token-type'),
		'editorOverviewRuler.infoForeground': css('--color-token-function'),
		'editorOverviewRuler.bracketMatchForeground': css('--color-muted-foreground'),
		'editorOverviewRuler.wordHighlightForeground': css('--color-selection'),
		'editorOverviewRuler.wordHighlightStrongForeground': css('--color-selection'),
		'editorOverviewRuler.wordHighlightTextForeground': css('--color-selection'),

		// --- multi cursor ---
		'editorMultiCursor.primary.foreground': css('--color-cursor'),
		'editorMultiCursor.primary.background': css('--color-editor'),
		'editorMultiCursor.secondary.foreground': css('--color-cursor'),
		'editorMultiCursor.secondary.background': css('--color-editor'),

		// --- global ---
		foreground: css('--color-foreground'),
		disabledForeground: css('--color-muted-foreground'),
		errorForeground: css('--color-destructive'),
		descriptionForeground: css('--color-muted-foreground'),
		'icon.foreground': css('--color-foreground'),
		focusBorder: css('--color-ring'),
		contrastBorder: css('--color-border'),
		'selection.background': css('--color-selection'),
		'sash.hoverBorder': css('--color-ring'),

		// --- widget shadows ---
		'widget.shadow': '#0000001a',
		'widget.border': css('--color-border'),
		'editorWidget.resizeBorder': css('--color-ring'),

		// --- widgets (find/replace, hover, suggest, etc.) ---
		'editorWidget.background': css('--color-popover'),
		'editorWidget.foreground': css('--color-popover-foreground'),
		'editorWidget.border': css('--color-border'),

		// --- input fields ---
		'input.background': css('--color-input'),
		'input.foreground': css('--color-input-foreground'),
		'input.border': css('--color-border'),
		'input.placeholderForeground': css('--color-muted-foreground'),
		'inputOption.activeBackground': css('--color-accent'),
		'inputOption.activeForeground': css('--color-accent-foreground'),
		'inputOption.activeBorder': css('--color-primary'),
		'inputOption.hoverBackground': css('--color-accent'),
		'inputValidation.infoBackground': css('--color-popover'),
		'inputValidation.infoBorder': css('--color-token-function'),
		'inputValidation.infoForeground': css('--color-popover-foreground'),
		'inputValidation.warningBackground': css('--color-popover'),
		'inputValidation.warningBorder': css('--color-token-type'),
		'inputValidation.warningForeground': css('--color-popover-foreground'),
		'inputValidation.errorBackground': css('--color-popover'),
		'inputValidation.errorBorder': css('--color-destructive'),
		'inputValidation.errorForeground': css('--color-destructive-foreground'),

		// --- dropdowns ---
		'dropdown.background': css('--color-popover'),
		'dropdown.foreground': css('--color-popover-foreground'),
		'dropdown.border': css('--color-border'),
		'dropdown.listBackground': css('--color-popover'),

		// --- buttons ---
		'button.background': css('--color-primary'),
		'button.foreground': css('--color-primary-foreground'),
		'button.hoverBackground': css('--color-primary'),
		'button.border': css('--color-border'),
		'button.separator': css('--color-primary-foreground'),
		'button.secondaryBackground': css('--color-secondary'),
		'button.secondaryForeground': css('--color-secondary-foreground'),
		'button.secondaryHoverBackground': css('--color-secondary'),

		// --- checkbox / radio ---
		'checkbox.background': css('--color-input'),
		'checkbox.foreground': css('--color-foreground'),
		'checkbox.border': css('--color-border'),
		'checkbox.selectBackground': css('--color-primary'),
		'checkbox.selectBorder': css('--color-primary'),
		'radio.activeBackground': css('--color-primary'),
		'radio.activeForeground': css('--color-primary-foreground'),
		'radio.activeBorder': css('--color-primary'),
		'radio.inactiveBackground': css('--color-input'),
		'radio.inactiveForeground': css('--color-foreground'),
		'radio.inactiveBorder': css('--color-border'),
		'radio.inactiveHoverBackground': css('--color-accent'),

		// --- lists (autocomplete, quick open, etc.) ---
		'list.activeSelectionBackground': css('--color-accent'),
		'list.activeSelectionForeground': css('--color-accent-foreground'),
		'list.activeSelectionIconForeground': css('--color-accent-foreground'),
		'list.focusBackground': css('--color-accent'),
		'list.focusForeground': css('--color-accent-foreground'),
		'list.focusOutline': css('--color-ring'),
		'list.focusAndSelectionOutline': css('--color-ring'),
		'list.hoverBackground': css('--color-accent'),
		'list.hoverForeground': css('--color-accent-foreground'),
		'list.inactiveSelectionBackground': css('--color-secondary'),
		'list.inactiveSelectionForeground': css('--color-secondary-foreground'),
		'list.inactiveSelectionIconForeground': css('--color-secondary-foreground'),
		'list.inactiveFocusBackground': css('--color-secondary'),
		'list.inactiveFocusOutline': css('--color-border'),
		'list.highlightForeground': css('--color-primary'),
		'list.focusHighlightForeground': css('--color-primary-foreground'),
		'list.invalidItemForeground': css('--color-destructive'),
		'list.errorForeground': css('--color-destructive'),
		'list.warningForeground': css('--color-token-type'),
		'list.deemphasizedForeground': css('--color-muted-foreground'),
		'list.filterMatchBackground': css('--color-selection'),
		'list.filterMatchBorder': '#00000000',
		'list.dropBackground': css('--color-accent'),
		'list.dropBetweenBackground': css('--color-ring'),
		'listFilterWidget.background': css('--color-popover'),
		'listFilterWidget.outline': css('--color-ring'),
		'listFilterWidget.noMatchesOutline': css('--color-destructive'),
		'listFilterWidget.shadow': '#0000001a',

		// --- tree ---
		'tree.indentGuidesStroke': css('--color-border'),
		'tree.inactiveIndentGuidesStroke': css('--color-line-highlight'),
		'tree.tableColumnsBorder': css('--color-border'),
		'tree.tableOddRowsBackground': css('--color-line-highlight'),

		// --- suggest widget ---
		'editorSuggestWidget.background': css('--color-popover'),
		'editorSuggestWidget.foreground': css('--color-popover-foreground'),
		'editorSuggestWidget.border': css('--color-border'),
		'editorSuggestWidget.selectedBackground': css('--color-accent'),
		'editorSuggestWidget.selectedForeground': css('--color-accent-foreground'),
		'editorSuggestWidget.selectedIconForeground': css('--color-accent-foreground'),
		'editorSuggestWidget.highlightForeground': css('--color-primary'),
		'editorSuggestWidget.focusHighlightForeground': css('--color-primary-foreground'),
		'editorSuggestWidgetStatus.foreground': css('--color-muted-foreground'),

		// --- hover widget ---
		'editorHoverWidget.background': css('--color-popover'),
		'editorHoverWidget.foreground': css('--color-popover-foreground'),
		'editorHoverWidget.border': css('--color-border'),
		'editorHoverWidget.statusBarBackground': css('--color-popover'),
		'editorHoverWidget.highlightForeground': css('--color-primary'),

		// --- action list (code actions / quick fix) ---
		'editorActionList.background': css('--color-popover'),
		'editorActionList.foreground': css('--color-popover-foreground'),
		'editorActionList.focusForeground': css('--color-accent-foreground'),
		'editorActionList.focusBackground': css('--color-accent'),

		// --- scrollbar ---
		'scrollbar.shadow': '#0000001a',
		'scrollbar.background': '#00000000',
		'scrollbarSlider.background': css('--color-muted'),
		'scrollbarSlider.hoverBackground': css('--color-ring'),
		'scrollbarSlider.activeBackground': css('--color-ring'),

		// --- progress bar ---
		'progressBar.background': css('--color-primary'),

		// --- badges ---
		'badge.background': css('--color-primary'),
		'badge.foreground': css('--color-primary-foreground'),

		// --- keybinding labels ---
		'keybindingLabel.background': css('--color-muted'),
		'keybindingLabel.foreground': css('--color-foreground'),
		'keybindingLabel.border': css('--color-border'),
		'keybindingLabel.bottomBorder': css('--color-border'),

		// --- menu ---
		'menu.background': css('--color-popover'),
		'menu.foreground': css('--color-popover-foreground'),
		'menu.selectionBackground': css('--color-accent'),
		'menu.selectionForeground': css('--color-accent-foreground'),
		'menu.selectionBorder': '#00000000',
		'menu.border': css('--color-border'),
		'menu.separatorBackground': css('--color-border'),

		// --- quick input ---
		'quickInput.background': css('--color-popover'),
		'quickInput.foreground': css('--color-popover-foreground'),
		'quickInputTitle.background': css('--color-popover'),
		'quickInputList.focusBackground': css('--color-accent'),
		'quickInputList.focusForeground': css('--color-accent-foreground'),
		'quickInputList.focusIconForeground': css('--color-accent-foreground'),
		'pickerGroup.foreground': css('--color-muted-foreground'),
		'pickerGroup.border': css('--color-border'),

		// --- marker navigation (go to error/warning) ---
		'editorMarkerNavigation.background': css('--color-popover'),
		'editorMarkerNavigationError.background': css('--color-destructive'),
		'editorMarkerNavigationError.headerBackground': css('--color-popover'),
		'editorMarkerNavigationWarning.background': css('--color-token-type'),
		'editorMarkerNavigationWarning.headerBackground': css('--color-popover'),
		'editorMarkerNavigationInfo.background': css('--color-token-function'),
		'editorMarkerNavigationInfo.headerBackground': css('--color-popover'),

		// --- text colors ---
		'textLink.foreground': css('--color-token-function'),
		'textLink.activeForeground': css('--color-token-function'),
		'textPreformat.foreground': css('--color-token-string'),
		'textPreformat.background': css('--color-line-highlight'),
		'textBlockQuote.background': css('--color-line-highlight'),
		'textBlockQuote.border': css('--color-border'),
		'textCodeBlock.background': css('--color-line-highlight'),
		'textSeparator.foreground': css('--color-border'),
	},
});

type Highlighter = Awaited<ReturnType<typeof createHighlighter>>;

let highlighter: Highlighter | null = null;
let initPromise: Promise<void> | null = null;

// lazy-init: load shiki + wire into Monaco once
export const ensureShiki = (): Promise<void> => {
	if (!initPromise) {
		initPromise = (async () => {
			const { createHighlighter } = await import('shiki/bundle/full');

			highlighter = await createHighlighter({
				themes: [buildTheme('light'), buildTheme('dark')],
				langs: [],
			});

			shikiToMonaco(highlighter, monaco);
		})();
	}
	return initPromise;
};

// re-apply themes after dark mode toggle — rebuilds theme objects from current CSS vars
export const updateShikiTheme = () => {
	if (!highlighter) return;

	// load the updated themes into shiki
	highlighter.loadTheme(buildTheme('light'));
	highlighter.loadTheme(buildTheme('dark'));

	// re-wire to pick up new theme data
	shikiToMonaco(highlighter, monaco);
};

// get the theme name for the current mode
export const currentThemeName = (): string =>
	document.documentElement.classList.contains('dark') ? THEME_DARK : THEME_LIGHT;

// load a language on demand if not already loaded
export const ensureLanguage = async (langId: string): Promise<void> => {
	if (!highlighter) return;
	const loaded = highlighter.getLoadedLanguages();
	if (loaded.includes(langId)) return;

	try {
		await highlighter.loadLanguage(langId as Parameters<Highlighter['loadLanguage']>[0]);
	} catch {
		// language not available in shiki — fall back to plaintext
		return;
	}

	// register the language with Monaco so shikiToMonaco can wire the token provider
	const known = monaco.languages.getLanguages().some((l) => l.id === langId);
	if (!known) monaco.languages.register({ id: langId });

	// re-wire so shikiToMonaco registers the token provider for the newly loaded language
	shikiToMonaco(highlighter, monaco);
};
